import { createMemo, Show, untrack, type ParentProps } from "solid-js"
import { useParams, type RouteSectionProps } from "@solidjs/router"
import { useGlobal } from "@/context/global"
import { useLanguage } from "@/context/language"
import { ServerConnection } from "@/context/server"
import { ServerSDKProvider } from "@/context/server-sdk"
import { ServerSyncProvider } from "@/context/server-sync"
import { requireServerKey } from "@/utils/session-route"
import { TargetSessionRouteContent } from "@/pages/session"

type RecoveryProps = Partial<RouteSectionProps> & { onReconnect?: () => void; reconnecting?: boolean; reconnectError?: boolean }

export function TargetServerRoute(props: ParentProps<RecoveryProps>) {
  const params = useParams<{ serverKey: string; id: string }>()
  const global = useGlobal()
  const language = useLanguage()
  const key = createMemo(() => requireServerKey(params.serverKey))
  const conn = createMemo(() => global.servers.list().find((item) => ServerConnection.key(item) === key()))
  return <Show when={conn() ? key() : undefined} keyed fallback={
    <section class="cowork-chat-connection-state" role="status">
      <h2>{language.t("cowork.chatDock.connectionUnavailable")}</h2>
      <p>{language.t("cowork.chatDock.connectionRecovery")}</p>
      <Show when={props.reconnectError}><p role="alert">{language.t("cowork.enterprise.chats.unavailable")}</p></Show>
      <Show when={props.onReconnect}><button type="button" class="accounting-button primary" disabled={props.reconnecting}
        onClick={() => props.onReconnect?.()}>{language.t(props.reconnecting ? "common.loading" : "cowork.enterprise.chats.continue")}</button></Show>
    </section>
  }>{(serverKey) => {
    const initial = untrack(conn)!
    const connection = () => {
      const current = conn()
      return current && ServerConnection.key(current) === serverKey ? current : initial
    }
    return <ServerSDKProvider server={connection}><ServerSyncProvider server={connection}>{props.children}</ServerSyncProvider></ServerSDKProvider>
  }}
  </Show>
}

export const TargetSessionRoute = (props: RecoveryProps = {}) => <TargetServerRoute {...props}><TargetSessionRouteContent /></TargetServerRoute>
